/**
 * Account Details Page Component
 * 
 * Detailed view and edit page for a single bank account.
 * Shows account information, allows editing, and displays activity logs.
 * 
 * Features:
 * - View and edit account details
 * - Toggle automation on/off
 * - Manage security Q&A pairs
 * - View activity logs from APLA workers
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getBank,
  updateBank,
  getProjectList,
  getKeyList,
  switchBankOn,
  switchBankOff,
  getBankLogs,
  getUserRole,
} from '../../services/api';
import { Form, Input, Button, Checkbox, Select, message, Spin, Alert, Table } from 'antd';
import './AccountDetailsPage.css';

const { Option } = Select;

const AccountDetailsPage = () => {
  const { id } = useParams();                             // Account ID from URL
  const navigate = useNavigate();
  const role = getUserRole();
  const isAxisOperator = role === 'AXIS_OPERATOR';
  
  // State management
  const [account, setAccount] = useState(null);           // Account data
  const [projects, setProjects] = useState([]);           // Available projects
  const [keys, setKeys] = useState([]);                   // Available parser keys
  const [editableAnswers, setEditableAnswers] = useState([]);  // Security Q&A
  const [logs, setLogs] = useState([]);                   // Activity logs
  const [loading, setLoading] = useState(true);           // Loading state
  const [logLoading, setLogLoading] = useState(false);    // Log loading state
  const [error, setError] = useState(null);               // Error message
  const [form] = Form.useForm();                          // Ant Design form instance

  /**
   * Fetches parser keys for the specified bank.
   * @param {string} bankName - Bank identifier
   */
  const fetchKeys = useCallback(async (bankName) => {
    try {
      const response = await getKeyList(bankName);
      const formattedKeys = response.data.map((key) => ({
        id: key.id,
        display: `${key.bank_name.toUpperCase()} | ${key.type.toUpperCase()}`,
      }));
      setKeys(formattedKeys);
    } catch (err) {
      message.error('Error loading key list.');
    }
  }, []);

  /**
   * Fetches account details from the API.
   */
  const fetchAccountDetails = useCallback(async () => {
    try {
      const response = await getBank(id);
      setAccount(response.data);
      setEditableAnswers(response.data.answers || []);
      form.setFieldsValue(response.data);

      // Fetch keys for the account's bank
      if (response.data.name) {
        await fetchKeys(response.data.name);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem('jwtToken');
        message.error('Session has expired. Please log in again.');
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || 'Error loading data');
        console.error(err);
      }
    }
  }, [id, form, navigate, fetchKeys]);

  /**
   * Fetches available projects for the dropdown.
   */
  const fetchProjects = useCallback(async () => {
    try {
      const response = await getProjectList();
      setProjects(response.data);
    } catch (err) {
      message.error('Error loading project list.');
    }
  }, []);

  /**
   * Fetches activity logs for the account from Redis.
   */
  const fetchLogs = useCallback(async () => {
    setLogLoading(true);
    try {
      const response = await getBankLogs(id);
      setLogs(response.data || []);
    } catch (err) {
      message.error('Error loading logs.');
      console.error('Error fetching logs:', err);
    }
    setLogLoading(false);
  }, [id]);

  /**
   * Handles account update form submission.
   * @param {Object} values - Form values
   */
  const handleUpdate = async (values) => {
    // Filter out empty security answers
    const filteredAnswers = editableAnswers.filter((a) => a.key && a.value);

    const payload = {
      ...values,
      answers: filteredAnswers.length > 0 ? filteredAnswers : undefined,
      current_key_id: values.current_key_id || null,
    };

    try {
      await updateBank(id, payload);
      message.success('Account successfully updated.');
      fetchAccountDetails();
    } catch (err) {
      message.error(
        `Error updating account: ${JSON.stringify(err.response?.data || err.message)}`
      );
    }
  };

  /**
   * Toggles automation on/off for the account.
   */
  const toggleStatus = async () => {
    try {
      if (account.status === 'working') {
        await switchBankOff(id);
        message.success('Account turned off.');
        setAccount((prevAccount) => ({
          ...prevAccount,
          status: 'not_working',
        }));
      } else {
        await switchBankOn(id);
        message.success('Account turned on.');
        setAccount((prevAccount) => ({
          ...prevAccount,
          status: 'working',
        }));
      }
    } catch (err) {
      message.error('Error changing account status.');
    }
  };

  // Security answer management functions
  const handleAnswerChange = (index, field, value) => {
    const updated = [...editableAnswers];
    updated[index][field] = value;
    setEditableAnswers(updated);
  };

  const handleAddNewAnswer = () => {
    setEditableAnswers([...editableAnswers, { key: '', value: '' }]);
  };

  const handleDeleteAnswer = (index) => {
    const updated = editableAnswers.filter((_, i) => i !== index);
    setEditableAnswers(updated);
  };

  // Fetch all data on component mount
  useEffect(() => {
    const token = sessionStorage.getItem('jwtToken');
    if (!token) {
      message.error('Authorization required.');
      navigate('/login');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchAccountDetails(), fetchProjects(), fetchLogs()]);
      } catch (err) {
        message.error('Error loading data.');
      }
      setLoading(false);
    };

    loadData();
  }, [id, fetchAccountDetails, fetchProjects, fetchLogs, navigate]);

  // Show loading spinner while data is being fetched
  if (loading) return <Spin />;
  if (error) return <Alert message="Error" description={error} type="error" showIcon />;

  return (
    <div className="account-details-container">
      <div className="content">
        {/* Screenshot and status section */}
        <div className="screenshot-container">
          <h3>Screenshot</h3>
          <img
            src={account.screenshot_url || 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ-dDGHe_jw2OkenlrT5E6414O3IQv_6Y7JrA&s'}
            alt="Screenshot"
            className="screenshot"
          />
          <h3>Status</h3>
          <p className={account.status === 'working' ? 'status-on' : 'status-off'}>
            {account.status === 'working' ? 'Working' : 'Not working'}
          </p>
          <Button type="primary" onClick={toggleStatus}>
            {account.status === 'working' ? 'Turn off' : 'Turn on'}
          </Button>
        </div>

        {/* Edit form section */}
        <div className="form-container">
          <h2>Edit account</h2>
          <Form form={form} layout="vertical" onFinish={handleUpdate}>
            <Form.Item name="bank_login" label="Login">
              <Input />
            </Form.Item>
            <Form.Item name="account_name" label="Account name">
              <Input />
            </Form.Item>
            <Form.Item name="phone_number" label="Phone number">
              <Input />
            </Form.Item>
            <Form.Item name="account_number" label="Account number">
              <Input />
            </Form.Item>
            <Form.Item name="corporate" valuePropName="checked">
              <Checkbox>Corporate account</Checkbox>
            </Form.Item>
            {!isAxisOperator && (
              <>
                <Form.Item name="project_id" label="Project">
                  <Select placeholder="Select a project">
                    {projects.map((project) => (
                      <Option key={project.id} value={project.id}>
                        {project.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="current_key_id" label="Key">
                  <Select placeholder="Select a key">
                    {keys.map((key) => (
                      <Option key={key.id} value={key.id}>
                        {key.display}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            {/* Security answers section */}
            <h3>Secret pairs</h3>
            <Table
              dataSource={editableAnswers}
              columns={[
                {
                  title: 'Key',
                  dataIndex: 'key',
                  render: (text, record, index) => (
                    <Input
                      value={text}
                      onChange={(e) => handleAnswerChange(index, 'key', e.target.value)}
                      placeholder="Key"
                    />
                  ),
                },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  render: (text, record, index) => (
                    <Input
                      value={text}
                      onChange={(e) => handleAnswerChange(index, 'value', e.target.value)}
                      placeholder="Value"
                    />
                  ),
                },
                {
                  title: 'Actions',
                  render: (_, __, index) => (
                    <Button danger onClick={() => handleDeleteAnswer(index)}>
                      Delete
                    </Button>
                  ),
                },
              ]}
              rowKey={(record, index) => index}
              pagination={false}
            />
            <Button type="dashed" onClick={handleAddNewAnswer} style={{ marginTop: '10px' }}>
              Add a new pair
            </Button>
            <Button type="primary" htmlType="submit" style={{ marginTop: '20px' }}>
              Update
            </Button>
          </Form>
        </div>
      </div>

      {/* Activity logs section */}
      <div className="logs-container">
        <h2>Account Logs</h2>
        <Button type="primary" onClick={fetchLogs} loading={logLoading}>
          Refresh Logs
        </Button>
        <Table
          dataSource={logs}
          columns={[
            {
              title: 'Timestamp',
              dataIndex: 'timestamp',
              key: 'timestamp',
              render: (text) => {
                // Convert UTC to Indian Standard Time
                const utcDate = new Date(text + 'Z');
                return utcDate.toLocaleString('en-IN', {
                  timeZone: 'Asia/Kolkata',
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true,
                });
              },
            },
            {
              title: 'Message',
              dataIndex: 'message',
              key: 'message',
            },
          ]}
          rowKey={(record, index) => index}
          pagination={{ pageSize: 10 }}
          style={{ marginTop: '20px' }}
        />
      </div>
    </div>
  );
};

export default AccountDetailsPage;
